import { Location } from '@angular/common'
import { Apollo } from 'apollo-angular'
import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { Router } from '@angular/router'

import * as moment from 'moment-timezone'
import {
  GenerateReportPreset,
  ReportService,
} from 'src/app/core/report.service'
import { TerminologyService } from 'src/app/terminology.service'
import { TimezoneService } from 'src/app/timezone.service'
import { TrendsService } from 'src/app/trends.service'
import {
  PHANTOM_DRIVEN_REPORT_TYPES,
  REPORT_TYPES_BY_MODALITY,
  REPORT_TYPE_PERIOD_MONTHS,
  isReportModality,
  isReportType,
} from 'common/reportTypes'
import { trendsScanners_scanner } from '../trends/types/trendsScanners'
import { getLatestSurveyIdGql } from './generate-report.gql'
import { PageHeaderAction } from '../page-header/page-header.component'
import { reportHandled } from 'src/app/core/errors/issueTracker'

/** getLatestSurveyId's answer for a scanner that has no form yet */
const NO_SURVEY_FOUND = 'no survey found'

@Component({
  selector: 'app-generate-report',
  templateUrl: './generate-report.component.html',
  styleUrls: ['./generate-report.component.scss'],
})
export class GenerateReportComponent implements OnInit {
  title = 'Generate Report'
  actions: PageHeaderAction[] = [{ text: 'Back' }]
  disableOptions = true
  disableGenerateButton = true // true = button disabled

  selectedDevice
  selectedModality = '---'
  selectedPeriod: moment.Moment
  selectedReportType: string

  devices: trendsScanners_scanner[]
  periodLengthInMonths: number
  allRoomNames: string[]
  periods: moment.Moment[]
  periodStart
  periodEnd

  reportTypes: string[] = ['---']

  @Output() saveEvent = new EventEmitter()

  objectKeys = Object.keys

  /** Set when another page opened this form with a device already chosen. */
  private preset: GenerateReportPreset | undefined

  constructor(
    private apollo: Apollo,
    private timezoneService: TimezoneService,
    private reportService: ReportService,
    private trendsService: TrendsService,
    private router: Router,
    private location: Location,
    private terms: TerminologyService,
  ) {
    // Navigation state is only readable while the navigation is in flight,
    // i.e. here in the constructor (same pattern as DatagridComponent).
    this.preset = this.router.getCurrentNavigation()?.extras?.state?.preset
  }

  ngOnInit() {
    this.periods = this.generateMonths()
    this.fetchScanners()
  }

  async fetchScanners() {
    const { scanners, roomNames } = await this.trendsService.allScanners()
    this.devices = scanners
    this.allRoomNames = roomNames
    if (this.preset) {
      this.applyPreset(this.preset)
    }
  }

  /**
   * Select the preset device, report type and period exactly as if the user
   * had picked them, so the form arrives ready to generate.
   */
  private applyPreset(preset: GenerateReportPreset) {
    const device = this.devices.find((d) => d.id === preset.scannerId)
    if (!device) {
      return
    }
    this.selectedDevice = device
    this.handleDeviceChange()
    if (preset.reportType && this.reportTypes.includes(preset.reportType)) {
      this.selectedReportType = preset.reportType
      this.handleModalityChange()
    }
    if (preset.periodEnd) {
      const wanted = moment(preset.periodEnd).tz(this.timezoneService.cachedTz)
      const period = this.periods.find((p) => p.isSame(wanted, 'month'))
      if (period) {
        this.selectedPeriod = period
        this.handlePeriodChange()
      }
    }
  }

  generateMonths(): moment.Moment[] {
    let result: moment.Moment[] = []
    for (let i = 0; i < 14; i += 1) {
      const thisTestDateStartOfMonth = moment()
        .tz(this.timezoneService.cachedTz)
        .add(-i, 'month')
        .startOf('month')
      result.push(thisTestDateStartOfMonth)
    }
    return result
  }

  handleDeviceChange() {
    this.selectedModality = this.selectedDevice.modality.id

    // Populate reportType based on modality
    this.handleReportTypeChoices()

    // Pick first choice by default
    this.selectedReportType = this.reportTypes[0]

    // Enable option selection
    if (this.selectedDevice) this.disableOptions = false
  }

  handleReportTypeChoices() {
    this.reportTypes = isReportModality(this.selectedModality)
      ? [...REPORT_TYPES_BY_MODALITY[this.selectedModality]]
      : []
    this.selectedReportType = this.reportTypes[0]
    this.handleModalityChange()
  }

  handleModalityChange() {
    this.handleReportNumberOfMonths()

    if (this.selectedPeriod) this.calculatePeriod()

    // Enable generate Button
    if (this.selectedDevice && this.selectedPeriod && this.selectedReportType) {
      this.disableGenerateButton = false
    }
  }

  handlePeriodChange() {
    if (this.selectedPeriod) this.calculatePeriod()

    // Enable generate Button
    if (this.selectedDevice && this.selectedPeriod && this.selectedReportType) {
      this.disableGenerateButton = false
    }
  }

  handleReportNumberOfMonths() {
    this.periodLengthInMonths = isReportType(this.selectedReportType)
      ? REPORT_TYPE_PERIOD_MONTHS[this.selectedReportType]
      : undefined
  }

  handleNumberOfMonthsChange() {
    if (this.periodLengthInMonths < 1) this.periodLengthInMonths = 1
    if (this.selectedPeriod) this.calculatePeriod()
  }

  calculatePeriod() {
    const periodLength = this.periodLengthInMonths - 1
    this.periodStart = this.selectedPeriod
      .clone()
      .startOf('M')
      .add(-periodLength, 'M')
      .format('MMM DD, YYYY')

    const today = moment()
    const monthEnd = this.selectedPeriod.clone().endOf('M')
    if (today < monthEnd) {
      this.periodEnd = today.format('MMM DD, YYYY')
    } else {
      this.periodEnd = monthEnd.format('MMM DD, YYYY')
    }
  }

  async generate() {
    const month = this.selectedPeriod.toDate()
    const selectedRoutine: string = this.selectedReportType
    const scannerId: number = this.selectedDevice.id
    let surveyTestId: number | undefined
    if (!PHANTOM_DRIVEN_REPORT_TYPES.has(selectedRoutine)) {
      try {
        surveyTestId = await this.getLatestSurveyId(scannerId)
      } catch (err) {
        // Survey-driven reports cannot proceed without a SurveyTest. The user
        // is told there is none whatever went wrong, so report what did,
        // unless the scanner simply has no form yet.
        if (!(err instanceof Error && err.message === NO_SURVEY_FOUND)) {
          reportHandled(err, 'find the latest form for a report')
        }
        // the report's name is data: only the sentence is reworded
        window.alert(
          `Cannot generate "${selectedRoutine}": ${this.terms.apply(
            'no form found for this scanner.',
          )}`,
        )
        return
      }
    }

    const periodLength: number = this.periodLengthInMonths
    this.reportService.generateReport({
      scannerId,
      surveyTestId,
      month,
      selectedRoutine,
      periodLength,
    })
  }

  getLatestSurveyId(scannerId) {
    return new Promise<number>((resolve, reject) => {
      const variables = { scannerId }
      const query = getLatestSurveyIdGql
      this.apollo
        .query<any>({ query, variables })
        .subscribe({
          next: (result) => {
            if (result.errors) {
              reject(result.errors[0])
              return
            }
            if (!result.data.survey_test[0]) {
              reject(new Error(NO_SURVEY_FOUND))
              return
            }
            resolve(result.data.survey_test[0].id)
          },
          error: (e) => reject(e),
        })
    })
  }

  back() {
    if (this.saveEvent.observers.length > 0) {
      // Embedded in the Reports page, which toggles back to its list.
      this.saveEvent.emit('Back')
    } else {
      // Routed directly (/home/reports/generate): return to the caller.
      this.location.back()
    }
  }

  actionsHandler($event: string) {
    switch ($event) {
      case 'Back':
        this.back()
        break
      default:
        break
    }
  }
}
